// LocaleProvider.tsx — mounted once in main.tsx, ABOVE the router, so the auth pages
// (/sign-in, /invite/:token, /unsubscribe) are inside it too.
//
// `locale` is derived from `preference` during render, EXCEPT for "system", which has a second
// input the render has no dependency on: the browser's own language. That input is tracked in
// its own state (`systemLocale`), updated only from the `languagechange` listener's callback —
// never synchronously in the effect body itself, which is what `react-hooks/set-state-in-effect`
// flags (a setState called directly in an effect, rather than from a subscription callback,
// risks cascading renders; see https://react.dev/learn/you-might-not-need-an-effect). Same shape
// as `initTheme`'s matchMedia listener — follow the system live, but only while the user has not
// taken a side.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { LocaleContext } from './context';
import {
  CATALOGS,
  browserLocale,
  getLocalePreference,
  setLocalePreference,
  type Locale,
  type LocalePreference,
} from './index';

export function LocaleProvider({
  children,
  /** Tests only: render a subtree in a fixed locale without touching localStorage. */
  initialPreference,
}: {
  children: ReactNode;
  initialPreference?: LocalePreference;
}) {
  const [preference, setPref] = useState<LocalePreference>(
    () => initialPreference ?? getLocalePreference(),
  );
  const [systemLocale, setSystemLocale] = useState<Locale>(() => browserLocale());

  // Re-sync `systemLocale` the instant `preference` becomes "system" — React's documented
  // adjust-state-during-render pattern (https://react.dev/learn/you-might-not-need-an-effect
  // #adjusting-some-state-when-a-prop-changes), not an effect, so it isn't the setState-in-
  // effect cascade the rule above is guarding against. Without this, switching INTO "system"
  // would show a `systemLocale` left over from mount instead of the browser's current language.
  const [syncedFor, setSyncedFor] = useState(preference);
  if (preference !== syncedFor) {
    setSyncedFor(preference);
    if (preference === 'system') setSystemLocale(browserLocale());
  }

  const locale = useMemo<Locale>(
    () => (preference === 'system' ? systemLocale : preference),
    [preference, systemLocale],
  );

  useEffect(() => {
    if (preference !== 'system') return;
    const onLanguageChange = () => setSystemLocale(browserLocale());
    window.addEventListener('languagechange', onLanguageChange);
    return () => window.removeEventListener('languagechange', onLanguageChange);
  }, [preference]);

  // `<html lang>` drives screen-reader pronunciation and the browser's own translate prompt;
  // index.html ships `lang="en"` and this keeps it honest after a switch.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setPreference = useCallback((next: LocalePreference) => {
    setLocalePreference(next);
    setPref(next);
  }, []);

  const value = useMemo(
    () => ({ locale, preference, t: CATALOGS[locale], setPreference }),
    [locale, preference, setPreference],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
